import CancelIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSeniorityList, getLatestPubSLEvent } from "../api/seniority";
import { isUserAuthorizedByServer } from "../api/userAccess";
import PageLoading from "../components/PageLoading";
import SeniorityListDetailTable from "../components/SeniorityListDetailTable";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import ViewCommentsDialog from "../components/ViewCommentsDialog";
import { getAdminStation, getMultiStationAdminStationList, sortOnCaseSensitiveTexts } from "../util/commonUtils";
import {
  ALL,
  BREADCRUMBS_TITLE,
  CONTRACT_SENIORITY_LIST_TYPE_MAPPER,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  GET_IAM_SENIORITY_LIST_TYPE_FOR_API,
  GET_IAM_SENIORITY_LIST_TYPE_FOR_CLIENT,
  API_TIMEOUT,
  API_POLLING_INTERVAL,
  SENIORITY_LIST_TABS,
} from "../util/constants";
import {
  PUBLISH_ERROR_TITLE,
  SUCCESS_TITLE,
  PUBLISH_SENIORITY_LIST_MESSAGE,
  PUBLISH_SENIORITY_LIST_ERROR_MESSAGE,
} from "../util/messageConstants";
import PublishSeniorityListDialog from "../components/PublishSeniorityListDialog";
import { seniorityDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import { filterSeniorityList, filterAllIssuesSeniorityList } from "../util/filterSeniorityList";
import { validateShiftWorkgroup, validateStation } from "../util/inputValidation";
import { SENIORITY_LIST_TEMP_MOBILE_MESSAGE } from "../util/messageConstants";
import { initializeSearchIndex, searchSeniorityList } from "../util/searchSeniorityList";
import { exportToCsv } from "../util/seniorityListExport";
import { generateExportedFilename } from "../util/seniorityListUtils";
import { getUnionContractGivenWorkgroup } from "../util/workgroupMapper";
import { uploadFileToS3, getPresignedUrl } from "../api/system";
import { buildPubSLS3Data } from "../util/pubSLEventBuilder";
import InProgressSeniorityListPage from "../components/InProgressSeniorityList";
import LastPublishedSeniorityListPage from "../components/LastPublishedSeniorityList";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../util/timeZoneService";
import { DateTime } from "luxon";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice";

const SENIORITY_LIST_TABLE_HEIGHT = "53vh";
const SENIORITY_LIST_TABLE_HEIGHT_WITH_WARNINGS = "48vh";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: "auto",
    position: "fixed",
    width: "100%",
  },
  breadCrumb: {
    marginLeft: "2.5vw",
  },
  desktopTitle: {
    marginLeft: "4.2vw",
  },
  seniorityListFilterAndTitle: {
    minHeight: "170px",
  },
  seniorityList: {
    width: "92vw",
    marginLeft: "4.25vw",
  },
  textInput: {
    maxWidth: "17vw",
    margin: "auto",
    textAlign: "left",
  },
  stationTextInput: {
    width: "90px",
  },
  workGroupTextInput: {
    width: "180px",
  },
  seniorityListTypeTextInput: {
    width: "200px",
  },
  searchTextInput: {
    width: "250px",
  },
  seniorityInput: {
    margin: "5px",
  },
  seniorityInputRow: {
    marginTop: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginLeft: "3.9vw",
    marginRight: "3.9vw",
  },
  seniorityFilters: {
    display: "flex",
  },
  tabStyles: {
    marginLeft: "3.5vw",
    padding: "10px",
  },
}));

const SeniorityListPage = (props) => {
  const classes = useStyles();

  // Redux State
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupData = useSelector(selectWorkgroupData);
  const dispatch = useDispatch();

  // Seniority Data State
  const [seniorityList, setSeniorityList] = useState([]);

  const [filteredSeniorityList, setFilteredSeniorityList] = useState([]);
  const [displayedSeniorityList, setDisplayedSeniorityList] = useState([]);

  // UI State
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [exportsInprogressMenuAnchor, setExportsInprogressMenuAnchor] = useState(null);

  const [selectedTab, setSelectedTab] = useState(0);

  // Publish Seniority List State
  const [pubSLComments, setPubSLComments] = useState("");
  const [publishedSeniorityList, setPublishedSeniorityList] = useState([]);
  const [publishedFilteredSeniorityList, setPublishedFilteredSeniorityList] = useState([]);
  const [publishedDisplayedSeniorityList, setPublishedDisplayedSeniorityList] = useState([]);
  const [isPublishSLLoading, setIsPublishSLLoading] = useState(false);
  const [isPublishSLDialogOpen, setIsPublishSLDialogOpen] = useState(false);
  const [isPublishSuccessDialogOpen, setIsPublishSuccessDialogOpen] = useState(false);
  const [isErrorPublishSL, setIsErrorPublishSL] = useState(false);
  const [exportsPublishedMenuAnchor, setExportsPublishedMenuAnchor] = useState(null);
  const [publishedTimestamp, setPublishedTimestamp] = useState("");
  const [publishedPtCount, setPublishedPtCount] = useState("-");
  const [publishedFtCount, setPublishedFtCount] = useState("-");

  // Filter State
  const [seniorityListType, setSeniorityListType] = useState("");
  const [station, setStation] = useState("");
  const [stationTouched, setStationTouched] = useState(false);
  const [stationErrors, setStationErrors] = useState("");
  const [workgroup, setWorkgroup] = useState("");
  const [workgroupErrors, setWorkgroupErrors] = useState("");
  const [userContract, setUserContract] = useState("");
  const [generatedTimestamp, setGeneratedTimestamp] = useState("");
  const [publishedCriticalMissingDataCount, setPublishedCriticalMissingDataCount] = useState("-");
  const [publishedBy, setPublishedBy] = useState("");

  const [filterSetting, setFilterSetting] = useState(ALL);

  const [searchString, setSearchString] = useState("");
  const [isSearch, setIsSearch] = useState(false);
  const [ptCount, setPtCount] = useState("-");
  const [ftCount, setFtCount] = useState("-");

  const [criticalMissingDataCount, setCriticalMissingDataCount] = useState("-");
  const [missingDataCount, setMissingDataCount] = useState("-");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [issuesFilter, setIssuesFilter] = useState("");
  const [viewCommentsDialogOpen, setViewCommentsDialogOpen] = useState(false);
  const isApiSubscribedRef = useRef(true);
  const stationFilterRef = useRef("");
  const workgroupFilterRef = useRef("");
  const seniorityListTypeFilterRef = useRef("");

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      stationFilterRef.current = stationFilter;
      const station = getAdminStation(props.featureConfig.userStations, userSession, stationFilter);
      setStation(station);
    }
    return () => {
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    initializeSearchIndex(filteredSeniorityList);
    const newSearchStringTrimmed = searchString.trim().toLowerCase();

    if (isSearch) {
      searchSeniorityList({
        searchString: newSearchStringTrimmed,
        applySearchCallback: applySearch,
        contract: userContract,
        workgroup,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSeniorityList]);

  useEffect(() => {
    initializeSearchIndex(publishedFilteredSeniorityList);
    const newSearchStringTrimmed = searchString.trim().toLowerCase();

    if (isSearch) {
      searchSeniorityList({
        searchString: newSearchStringTrimmed,
        applySearchCallback: applySearch,
        contract: userContract,
        workgroup,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedFilteredSeniorityList]);
  useEffect(() => {
    setSearchString("");
  }, [selectedTab, station, workgroup, seniorityListType]);

  useEffect(() => {
    if (station !== "" && workgroup !== "") {
      updateSeniorityList(station, workgroup, seniorityListType, userContract);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, station, workgroup, seniorityListType]);

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === stationFilterRef.current &&
      responseQueryParams.workgroup === workgroupFilterRef.current &&
      (GET_IAM_SENIORITY_LIST_TYPE_FOR_CLIENT[responseQueryParams.seniorityListType] ===
        seniorityListTypeFilterRef.current ||
        responseQueryParams.seniorityListType === seniorityListTypeFilterRef.current)
    );
  };

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const updateSeniorityList = (station, workgroup, seniorityListType, contract) => {
    isApiSubscribedRef.current = true;
    stationFilterRef.current = station;
    workgroupFilterRef.current = workgroup;
    seniorityListTypeFilterRef.current = seniorityListType;
    setIsLoading(true);
    setIsError(false);
    setErrorObject({});
    setFilteredSeniorityList([]);
    setMissingDataCount("-");
    setCriticalMissingDataCount("-");
    setIssuesFilter("");
    setFilterSetting(ALL);
    if (selectedTab === 0) {
      setSeniorityList([]);
      setFtCount("-");
      setPtCount("-");
      setDisplayedSeniorityList([]);
      setPubSLComments("");
      fetchSeniorityList(
        station,
        workgroup,
        GET_IAM_SENIORITY_LIST_TYPE_FOR_API[seniorityListType] || seniorityListType
      )
        .then((res) => res.json())
        .then((result) => {
          if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
            if (result.message) {
              console.error("API response came back with a message: ", result.message);
              throw new Error("Unexpected error");
            } else {
              const scrubbedSeniorityList = [...result.ineligibleEmployees, ...result.eligibleEmployees];
              setSeniorityList(scrubbedSeniorityList);
              const filteredSeniorityList = filterSeniorityList(scrubbedSeniorityList, ALL);
              setFilteredSeniorityList(filteredSeniorityList);
              setDisplayedSeniorityList(filteredSeniorityList);
              setGeneratedTimestamp(parseInt(result.generatedTimestamp));
              setPtCount(result.partTimeCount);
              setFtCount(result.fullTimeCount);
              setCriticalMissingDataCount(result.criticalMissingDataCount);
              setMissingDataCount(result.missingDataCount);
              setIsLoading(false);
            }
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsLoading(false);
          setIsError(true);
          setErrorObject(seniorityDataErrorCodeToErrorMessageMap.unexpectedSeniorityDataError);
        });
    } else if (selectedTab === 1) {
      setPublishedSeniorityList([]);
      setPublishedDisplayedSeniorityList([]);
      setPublishedFtCount("-");
      setPublishedPtCount("-");
      setPubSLComments("");
      getLatestPubSLEvent(station, workgroup, seniorityListType)
        .then((res) => res.json())
        .then((result) => {
          if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
            if (result.message) {
              console.error("API response came back with a message: ", result.message);
              throw new Error("Unexpected error");
            } else {
              setPublishedSeniorityList(result?.seniorityList);
              const filteredSeniorityList = filterSeniorityList(result?.seniorityList, ALL);
              setPublishedFilteredSeniorityList(filteredSeniorityList);
              setPublishedDisplayedSeniorityList(filteredSeniorityList);
              setPublishedTimestamp(parseInt(result?.createdAt));
              setPublishedPtCount(result?.partTimeCount);
              setPublishedFtCount(result?.fullTimeCount);
              setPublishedCriticalMissingDataCount(0);
              setIsLoading(false);
              setPublishedBy(result?.publishedBy);
              setPubSLComments(result?.comments);
            }
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsLoading(false);
          setIsError(true);
          setErrorObject(seniorityDataErrorCodeToErrorMessageMap.unexpectedSeniorityDataError);
        });
    }
  };

  // fetch SeniorityList till it matches requestId
  const longPollingPubSLFetch = (requestId) => {
    const startTime = DateTime.now();

    function fetchPubSL() {
      getLatestPubSLEvent(station, workgroup, seniorityListType)
        .then((result) => result.json())
        .then((res) => {
          console.log("fetchPubSL Result: ", res);
          if (res?.requestId === requestId) {
            setIsPublishSLDialogOpen(false);
            setIsPublishSLLoading(false);
            setIsErrorPublishSL(false);
            setIsPublishSuccessDialogOpen(true);
          } else if (!res || res.error) {
            // stop polling if one call fails
            setIsPublishSLDialogOpen(true);
            setIsPublishSLLoading(false);
            setIsPublishSuccessDialogOpen(false);
            setIsErrorPublishSL(true);
          } else {
            const currentTime = DateTime.now();
            const elapsedTime = currentTime - startTime;
            if (elapsedTime < API_TIMEOUT) {
              // recursive call till requestId matches or timeout happens
              setTimeout(fetchPubSL, API_POLLING_INTERVAL);
            } else {
              // stop polling when timeout
              setIsPublishSLDialogOpen(false);
              setIsPublishSLLoading(false);
              setIsErrorPublishSL(true);
              setIsPublishSuccessDialogOpen(true);
            }
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsPublishSLDialogOpen(false);
          setIsPublishSLLoading(false);
          setIsErrorPublishSL(true);
          setIsPublishSuccessDialogOpen(true);
        });
    }

    fetchPubSL();
  };

  const submitPubSLEvent = async () => {
    setIsPublishSLLoading(true);

    const createdAt = DateTime.now().toMillis().toString();
    const seniorityListFile = await buildPubSLS3Data(
      createdAt,
      workgroup,
      seniorityListType,
      station,
      pubSLComments,
      userSession,
      seniorityList
    );

    getPresignedUrl(
      "put_object",
      "published-seniority-lists",
      encodeURIComponent(
        `PUBLISHED_SENIORITY_LISTS/${station.replace(/\s+/g, "")}/${workgroup.replace(
          /\s+/g,
          ""
        )}/${seniorityListType.replace(/\s+/g, "")}/${createdAt}_list.json`
      )
    )
      .then((res) => res.json())
      .then((result) => {
        if (result) {
          if (result.presignedUrl) {
            uploadFileToS3(result.presignedUrl, seniorityListFile).then((response) => {
              if (response.status === 200) {
                console.log("Success!");
                const requestId = response.headers.get("x-amz-request-id");
                longPollingPubSLFetch(requestId);
              } else {
                console.error("Failure in bid S3 upload!");
                setIsPublishSLDialogOpen(false);
                setIsPublishSLLoading(false);
                setIsErrorPublishSL(true);
                setIsPublishSuccessDialogOpen(true);
              }
            });
          } else {
            console.error("Failure in getting S3 presigned url!");
            setIsPublishSLDialogOpen(false);
            setIsPublishSLLoading(false);
            setIsErrorPublishSL(true);
            setIsPublishSuccessDialogOpen(true);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsPublishSLDialogOpen(false);
        setIsPublishSLLoading(false);
        setIsErrorPublishSL(true);
        setIsPublishSuccessDialogOpen(true);
      });
  };

  const handleSeniorityListTypeChange = (event) => {
    setSeniorityListType(event.target.value);
    seniorityListTypeFilterRef.current = event.target.value;
  };

  const handleStationChange = (event) => {
    setStation(event.target.value);
    stationFilterRef.current = event.target.value;
    dispatch(updateStationFilter(event.target.value));
    const updatedSeniorityListType = CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, event.target.value)[0];
    setSeniorityListType(updatedSeniorityListType);
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
    setStationTouched(true);
  };

  const handleFilterSettingChange = (event, newValue) => {
    if (newValue !== null) {
      setFilterSetting(newValue);
      let filteredList = filterSeniorityList(selectedTab === 0 ? seniorityList : publishedSeniorityList, newValue);
      setIssuesFilter("");
      setFilteredSeniorityList(filteredList);
      if (selectedTab === 0) {
        setDisplayedSeniorityList(filteredList);
      }
      if (selectedTab === 1) {
        setPublishedDisplayedSeniorityList(filteredList);
      }
    }
  };

  const handleWorkgroupSelectChange = (event) => {
    setWorkgroup(event.target.value);
    workgroupFilterRef.current = event.target.value;
    const selectedWorkgroupUnionContract = getUnionContractGivenWorkgroup(event.target.value);
    const updatedSeniorityListType = CONTRACT_SENIORITY_LIST_TYPE_MAPPER(selectedWorkgroupUnionContract, station)[0];
    setSeniorityListType(updatedSeniorityListType);
    const workgroupErrors = validateShiftWorkgroup(event.target.value);
    setWorkgroupErrors(workgroupErrors);
    if (workgroupErrors === "") {
      setUserContract(selectedWorkgroupUnionContract);
    }
  };

  const handleWorkgroupSelectBlur = (event) => {
    setWorkgroupErrors(validateShiftWorkgroup(event.target.value));
  };

  const handleSearchChange = (event) => {
    setSearchString(event.target.value);
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(event.target.value);
    }
  };

  const handleSearchCancel = () => {
    setSearchString("");
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    const newSearchStringTrimmed = newSearchString.trim().toLowerCase();
    searchSeniorityList({
      searchString: newSearchStringTrimmed,
      applySearchCallback: applySearch,
      contract: userContract,
      workgroup,
    });
  };

  const applySearch = (searchString, searchedSeniorityList) => {
    if (searchString === "") {
      setIsSearch(false);
    } else {
      setIsSearch(true);
    }
    setDisplayedSeniorityList(searchedSeniorityList);
    setPublishedDisplayedSeniorityList(searchedSeniorityList);
  };

  const handleIssuesFilter = (issueType) => {
    //unselect issues filter if it is clicked 2 times
    setFilterSetting("");
    if (issueType === issuesFilter) {
      setIssuesFilter("");
      setFilterSetting(ALL);
      setFilteredSeniorityList(seniorityList);

      if (isSearch) {
        handleSearchSubmit(searchString);
      } else {
        setDisplayedSeniorityList(seniorityList);
      }
    } else {
      let filteredList = filterAllIssuesSeniorityList(seniorityList, issueType);
      setIssuesFilter(issueType);
      setFilteredSeniorityList(filteredList);
      setDisplayedSeniorityList(filteredList);
    }
  };

  const handleComment = () => {
    setViewCommentsDialogOpen(true);
  };
  const renderSeniorityListTypeItem = (seniorityListTypeString) => {
    return (
      <MenuItem key={seniorityListTypeString} value={seniorityListTypeString}>
        {seniorityListTypeString}
      </MenuItem>
    );
  };

  const renderStationItem = (station) => {
    return (
      <MenuItem key={station} value={station} disabled={!props.featureConfig.userStations.includes(station)}>
        {station}
      </MenuItem>
    );
  };

  const renderWorkgroupItem = (workgroup) => {
    return (
      <MenuItem key={workgroup} value={workgroup}>
        {workgroup}
      </MenuItem>
    );
  };

  const renderSeniorityList = (seniorityList) => (
    <SeniorityListDetailTable
      seniorityList={seniorityList}
      contract={workgroupFilterRef.current ? getUnionContractGivenWorkgroup(workgroupFilterRef.current) : null}
      workgroup={workgroup}
      seniorityListType={seniorityListTypeFilterRef.current}
      station={stationFilterRef.current}
      isLoading={isLoading}
      isError={isError}
      errorObject={errorObject}
      seniorityListTableHeight={
        criticalMissingDataCount > 0 || missingDataCount > 0
          ? SENIORITY_LIST_TABLE_HEIGHT_WITH_WARNINGS
          : SENIORITY_LIST_TABLE_HEIGHT
      }
    />
  );

  const SearchButton = () => {
    return (
      <IconButton size="small" className="search-icon-button" onMouseDown={() => handleSearchSubmit(searchString)}>
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleSearchCancel}>
        <CancelIcon />
      </IconButton>
    );
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.SENIORITY_LIST_ADMIN];
  const breadcrumbsLink = [HOME_PATH];
  const renderMobileSeniorityListPage = () => {
    return (
      <div className="page-error" data-testid="seniority-list-mobile-page">
        {SENIORITY_LIST_TEMP_MOBILE_MESSAGE}
      </div>
    );
  };

  const handleExportBtnClick = (event) => {
    if (selectedTab === 0) {
      setExportsInprogressMenuAnchor(event.currentTarget);
    } else {
      setExportsPublishedMenuAnchor(event.currentTarget);
    }
  };

  const handleExportMenuClose = () => {
    if (selectedTab === 0) {
      setExportsInprogressMenuAnchor(null);
    } else {
      setExportsPublishedMenuAnchor(null);
    }
  };

  const handleCsvMenuItemClick = async (event) => {
    try {
      exportToCsv({
        filename: generateExportedFilename({
          station,
          workgroup,
          seniorityListType,
          generatedTimestamp: selectedTab === 0 ? generatedTimestamp : publishedTimestamp,
          fileType: "csv",
        }),
        rows: selectedTab === 0 ? seniorityList : publishedSeniorityList,
        contractType: userContract,
        seniorityListType,
        station,
        workgroup,
      });
      handleExportMenuClose();
    } catch (e) {
      console.error("Error during downloading csv!");
    }
  };

  const renderDesktopSeniorityListPage = () => {
    return (
      <div className={classes.root}>
        <div className={classes.breadCrumb}>
          <StarsBreadcrumbs
            isConfirmationRequired={false}
            breadcrumbsLinks={breadcrumbsLink}
            breadcrumbsList={breadcrumbsList}
          />
        </div>
        <div className={classes.seniorityListFilterAndTitle}>
          <div
            className={
              !props.isDesktopView ? `${classes.pageTitle} page-title-left` : `page-title-left ${classes.desktopTitle}`
            }
          >
            Seniority List Administration
          </div>
          <div className={classes.seniorityInputRow}>
            <div className={classes.seniorityFilters}>
              <div className={classes.seniorityInput}>
                {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                  <FormControl variant="outlined">
                    <InputLabel>Station</InputLabel>
                    <Select
                      id="stationSelectElement"
                      value={station}
                      className={`${classes.textInput} ${classes.stationTextInput}`}
                      onChange={handleStationChange}
                      onBlur={handleStationBlur}
                      label="Station"
                      error={!!(stationTouched && stationErrors)}
                    >
                      <MenuItem value="" disabled>
                        <em>Station</em>
                      </MenuItem>
                      {getMultiStationAdminStationList(userSession)
                        .slice()
                        .sort()
                        .map((station) => {
                          return renderStationItem(station);
                        })}
                    </Select>
                    {stationTouched && stationErrors ? <FormHelperText error>{stationErrors}</FormHelperText> : ""}
                  </FormControl>
                ) : (
                  <TextField
                    data-testid="stationSelectElement"
                    className={
                      props.isDesktopView
                        ? `${classes.textInput} ${classes.stationTextInput}`
                        : classes.stationTextField
                    }
                    label="Station"
                    variant="outlined"
                    value={station}
                    disabled
                  />
                )}
              </div>
              <div className={classes.seniorityInput}>
                <FormControl variant="outlined">
                  <InputLabel id="workgroupSelectLabel">Workgroup</InputLabel>
                  <Select
                    labelId="workgroupSelectLabel"
                    id="workgroupSelectElement"
                    data-testid="workgroup-select-label"
                    className={`${classes.textInput} ${classes.workGroupTextInput}`}
                    value={workgroup}
                    onChange={handleWorkgroupSelectChange}
                    onBlur={handleWorkgroupSelectBlur}
                    label="Workgroup"
                    error={!!(workgroupErrors !== "")}
                  >
                    <MenuItem value="" disabled>
                      <em>Workgroup</em>
                    </MenuItem>
                    {Object.keys(workgroupData)
                      .slice()
                      .sort((a, b) => sortOnCaseSensitiveTexts(a, b))
                      .map((workgroup) => {
                        return renderWorkgroupItem(workgroup);
                      })}
                  </Select>
                  {workgroupErrors !== "" && <FormHelperText error>{workgroupErrors}</FormHelperText>}
                </FormControl>
              </div>

              <div className={classes.seniorityInput}>
                <FormControl
                  variant="outlined"
                  className={classes.selectInput}
                  data-testid="seniorityListTypeSelectElement"
                >
                  <InputLabel>Seniority List Type</InputLabel>
                  <Select
                    id="seniorityListTypeSelectElement"
                    className={`${classes.textInput} ${classes.seniorityListTypeTextInput}`}
                    value={seniorityListType}
                    onChange={handleSeniorityListTypeChange}
                    label="Seniority List Type"
                    disabled={!workgroup}
                  >
                    <MenuItem value="" disabled>
                      <em>Seniority List Type</em>
                    </MenuItem>
                    {userContract !== "" &&
                      CONTRACT_SENIORITY_LIST_TYPE_MAPPER(userContract, station)
                        .slice()
                        .sort()
                        .map((seniorityListType) => {
                          return renderSeniorityListTypeItem(seniorityListType);
                        })}
                  </Select>
                </FormControl>
              </div>
              <div className={classes.seniorityInput} data-testid="searchSelectElement">
                <TextField
                  id="searchSelectElement"
                  className={`${classes.textInput} ${classes.searchTextInput}`}
                  label="Search"
                  variant="outlined"
                  value={searchString}
                  onChange={handleSearchChange}
                  onBlur={handleSearchBlur}
                  disabled={!workgroup}
                  InputProps={{
                    endAdornment:
                      searchString !== "" ? (
                        <InputAdornment position="end">
                          <CancelButton />
                          <SearchButton />
                        </InputAdornment>
                      ) : (
                        <InputAdornment position="end">
                          <SearchButton />
                        </InputAdornment>
                      ),
                  }}
                  onKeyUp={handleSearchEnterKeyPressed}
                />
              </div>
            </div>
          </div>
          <div className={classes.tabStyles}>
            <Tabs value={selectedTab} onChange={handleChangeTab} sx={{ width: "inherit", margin: "auto" }}>
              <Tab sx={{ fontWeight: "bold" }} label={SENIORITY_LIST_TABS.IN_PROGRESS}></Tab>
              <Tab sx={{ fontWeight: "bold" }} label={SENIORITY_LIST_TABS.LAST_PUBLISHED}></Tab>
            </Tabs>
          </div>
          <div hidden={selectedTab !== 0}>
            <InProgressSeniorityListPage
              selectedTab={selectedTab}
              userSession={userSession}
              criticalMissingDataCount={criticalMissingDataCount}
              handleExportMenuClose={handleExportMenuClose}
              displayedSeniorityList={displayedSeniorityList}
              exportsMenuAnchor={exportsInprogressMenuAnchor}
              ftCount={ftCount}
              ptCount={ptCount}
              handleFilterSettingChange={handleFilterSettingChange}
              renderSeniorityList={renderSeniorityList}
              handleStationChange={handleStationChange}
              handleStationBlur={handleStationBlur}
              stationTouched={stationTouched}
              stationErrors={stationErrors}
              handleWorkgroupSelectBlur={handleWorkgroupSelectBlur}
              handleWorkgroupSelectChange={handleWorkgroupSelectChange}
              workgroupErrors={workgroupErrors}
              renderWorkgroupItem={renderWorkgroupItem}
              renderStationItem={renderStationItem}
              renderSeniorityListTypeItem={renderSeniorityListTypeItem}
              searchString={searchString}
              CancelButton={CancelButton}
              SearchButton={SearchButton}
              handleSearchEnterKeyPressed={handleSearchEnterKeyPressed}
              handleSeniorityListTypeChange={handleSeniorityListTypeChange}
              handleSearchChange={handleSearchChange}
              handleSearchBlur={handleSearchBlur}
              setIsPublishSLDialogOpen={setIsPublishSLDialogOpen}
              isLoading={isLoading}
              handleCsvMenuItemClick={handleCsvMenuItemClick}
              seniorityList={seniorityList}
              missingDataCount={missingDataCount}
              filterSetting={filterSetting}
              issuesFilter={issuesFilter}
              handleIssuesFilter={handleIssuesFilter}
            />
          </div>

          <div hidden={selectedTab !== 1}>
            <LastPublishedSeniorityListPage
              selectedTab={selectedTab}
              userSession={userSession}
              station={station}
              criticalMissingDataCount={publishedCriticalMissingDataCount}
              handleExportMenuClose={handleExportMenuClose}
              displayedSeniorityList={publishedDisplayedSeniorityList}
              exportsMenuAnchor={exportsPublishedMenuAnchor}
              ftCount={publishedFtCount}
              ptCount={publishedPtCount}
              handleFilterSettingChange={handleFilterSettingChange}
              renderSeniorityList={renderSeniorityList}
              handleStationChange={handleStationChange}
              handleStationBlur={handleStationBlur}
              stationTouched={stationTouched}
              stationErrors={stationErrors}
              workgroup={workgroup}
              handleWorkgroupSelectBlur={handleWorkgroupSelectBlur}
              handleWorkgroupSelectChange={handleWorkgroupSelectChange}
              workgroupErrors={workgroupErrors}
              renderWorkgroupItem={renderWorkgroupItem}
              renderStationItem={renderStationItem}
              userContract={userContract}
              seniorityListType={seniorityListType}
              renderSeniorityListTypeItem={renderSeniorityListTypeItem}
              searchString={searchString}
              CancelButton={CancelButton}
              SearchButton={SearchButton}
              handleSearchEnterKeyPressed={handleSearchEnterKeyPressed}
              handleSeniorityListTypeChange={handleSeniorityListTypeChange}
              handleSearchChange={handleSearchChange}
              handleSearchBlur={handleSearchBlur}
              setIsPublishSLDialogOpen={setIsPublishSLDialogOpen}
              isLoading={isLoading}
              handleExportBtnClick={handleExportBtnClick}
              handleCsvMenuItemClick={handleCsvMenuItemClick}
              seniorityList={publishedSeniorityList}
              publishedTimestamp={publishedTimestamp}
              filterSetting={filterSetting}
              handleComment={handleComment}
              publishedBy={publishedBy}
            />
          </div>
        </div>
        {isPublishSLDialogOpen && (
          <PublishSeniorityListDialog
            isDialogOpen={isPublishSLDialogOpen}
            onClickDialogClose={() => setIsPublishSLDialogOpen(false)}
            onSetComments={setPubSLComments}
            station={station}
            workgroup={workgroup}
            yesTitle={"Publish"}
            seniorityListType={seniorityListType}
            isPublishSLLoading={isPublishSLLoading}
            onSubmitPubSLEvent={submitPubSLEvent}
          />
        )}

        {isPublishSuccessDialogOpen && (
          <SuccessDialog
            isSuccessDialogOpen={isPublishSuccessDialogOpen}
            title={isErrorPublishSL ? PUBLISH_ERROR_TITLE : SUCCESS_TITLE}
            message={PUBLISH_SENIORITY_LIST_MESSAGE}
            redirectTo={props?.featureConfig?.adminPath}
            hasError={isErrorPublishSL}
            error={PUBLISH_SENIORITY_LIST_ERROR_MESSAGE}
            handleOKClick={() => {
              setIsPublishSuccessDialogOpen(false);
              setIsErrorPublishSL(false);
              setPubSLComments("");
              if (!isErrorPublishSL) setSelectedTab(1);
            }}
          />
        )}
        {viewCommentsDialogOpen ? (
          <ViewCommentsDialog
            isDialogOpen={viewCommentsDialogOpen}
            handleClickDialogClose={() => setViewCommentsDialogOpen(false)}
            header={`Last published by ${publishedBy}`}
            publishedTimestamp={getStationTimeZoneDateTimeStringFromUTCInteger(publishedTimestamp, station).toFormat(
              "MM/dd/yyyy T ZZZZ"
            )}
            message={pubSLComments}
          />
        ) : (
          ""
        )}
      </div>
    );
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div data-testid="seniority-list-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopSeniorityListPage() : renderMobileSeniorityListPage()}</>
      )}
    </div>
  );
};

export default SeniorityListPage;
